import dayjs from "dayjs";

/**
 * Format date to MM/DD/YYYY format
 * @param dateString - ISO date string
 * @returns Formatted date string (e.g., "11/01/2025")
 */
export const formatDateShort = (dateString: string | null | undefined): string => {
    if (!dateString) return '';
    
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        
        return `${month}/${day}/${year}`;
    } catch (error) {
        console.error('Error formatting date:', error);
        return '';
    }
};

export const formatDateTimeOffset = (date: Date): string => {
  return dayjs(date).format("YYYY-MM-DDTHH:mm:ss.SSSZ");
};

/**
 * Calculate time ago from a date string
 * Always counts in days, no matter how many days have passed
 * @param dateString - ISO date string
 * @returns Time ago string (e.g., "2 days ago", "3 hours ago", "45 days ago")
 */
export const getTimeAgo = (dateString: string | null | undefined): string => {
    if (!dateString) return '';

    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';

        const now = new Date();
        const diffInMs = now.getTime() - date.getTime();
        const diffInMinutes = Math.floor(diffInMs / (1000 * 60));

        if (diffInMinutes < 1) return 'Just now';
        if (diffInMinutes < 60) return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;

        const diffInHours = Math.floor(diffInMinutes / 60);
        if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;

        // Always count in days, no matter how many days
        const diffInDays = Math.floor(diffInHours / 24);
        return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    } catch (error) {
        console.error('Error calculating time ago:', error);
        return '';
    }
};

/**
 * Format last active date with time ago
 * Returns two lines:
 * Line 1: MM/DD/YYYY
 * Line 2: (X days ago)
 * 
 * @param dateString - ISO date string
 * @returns Object with formatted date and time ago
 */
export const formatLastActive = (dateString: string | null | undefined): { date: string; timeAgo: string } => {
    if (!dateString) {
        return { date: '', timeAgo: '' };
    }
    
    const date = formatDateShort(dateString);
    const timeAgo = getTimeAgo(dateString);
    
    return {
        date,
        timeAgo: timeAgo ? `(${timeAgo})` : ''
    };
};

